"use client";
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { pathnames } from '@/config';
import { useState, useEffect } from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { useRouter } from 'next/navigation';
import FullscreenModal from '@/components/ui/fullscreen-modal-kategori-posts';
import { getWPBaseUrl } from '@/config/site';

type PostBreadcrumbProps = {
    post: any;
    locale: string;
};

export default function PostBreadcrumb({ post, locale }: PostBreadcrumbProps) {
    const t = useTranslations('PostBreadcrumb');
    const router = useRouter();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isClient, setIsClient] = useState(false); // State to track if we're on the client side

    const blogPath = pathnames['/posts'][locale as keyof typeof pathnames['/posts']];
    const isRTL = locale === 'ar';

    // Get category if available
    const category = post._embedded?.['wp:term']?.find(
        (terms: any[]) => terms[0]?.taxonomy === 'category'
    )?.[0];

    const handleCategoryClick = async () => {
        if (!category) return;

        setIsModalOpen(true);
        setIsLoading(true);

        try {
            const baseUrl = getWPBaseUrl(locale);

            const response = await fetch(
                `${baseUrl}/posts?categories=${category.id}&_embed=1&per_page=20`,
                { next: { revalidate: 60 } }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch category posts');
            }

            const posts = await response.json();
            setRelatedPosts(posts);
        } catch (error) {
            console.error('Failed to fetch related posts:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const truncateTitle = (title: string, maxLength: number) => {
        if (title.length <= maxLength) return title;
        return title.substring(0, maxLength) + '...';
    };

    // Use effect to set client-side state after component mounts
    useEffect(() => {
        setIsClient(true); // Set the flag to true after the component mounts (on the client side)
    }, []);

    return (
        <>
            <nav className="w-full bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" aria-label="Breadcrumb">
                <div className="container mx-auto px-4 py-3">
                    <ol className={`flex flex-wrap items-center gap-2 ${isRTL ? 'rtl:space-x-reverse' : ''}`}>
                        {/* Home */}
                        <li className="inline-flex items-center">
                            <Link
                                href={`/${locale}`}
                                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white transition-colors"
                            >
                                <svg
                                    className="w-3.5 h-3.5 me-2.5"
                                    aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
                                </svg>
                                {t('home')}
                            </Link>
                        </li>

                        {/* Blog */}
                        <li className="flex items-center">
                            <svg
                                className={`w-3 h-3 text-gray-400 mx-1 ${isRTL ? 'rtl:rotate-180' : ''}`}
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 6 10"
                            >
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
                            </svg>
                            <Link
                                href={`/${locale}${blogPath}`}
                                className="ms-1 text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white transition-colors"
                            >
                                {t('blog')}
                            </Link>
                        </li>

                        {/* Category (if available) */}
                        {category && (
                            <li className="flex items-center">
                                <svg
                                    className={`w-3 h-3 text-gray-400 mx-1 ${isRTL ? 'rtl:rotate-180' : ''}`}
                                    aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 6 10"
                                >
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
                                </svg>
                                <button
                                    onClick={handleCategoryClick}
                                    className="ms-1 text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    {category.name}
                                </button>
                            </li>
                        )}

                        {/* Current Post */}
                        <li className="flex items-center flex-1 min-w-0" aria-current="page">
                            <svg
                                className={`flex-shrink-0 w-3 h-3 text-gray-400 mx-1 ${isRTL ? 'rtl:rotate-180' : ''}`}
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 6 10"
                            >
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
                            </svg>
                            <span className="ms-1 text-sm font-medium text-gray-500 truncate dark:text-gray-400" title={post.title.rendered}>
                                {isClient ? truncateTitle(post.title.rendered, window.innerWidth <= 768 ? 30 : 50) : post.title.rendered}
                            </span>
                        </li>
                    </ol>
                </div>
            </nav>

            <FullscreenModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={t('relatedPosts', { category: category?.name })}
            >
                {isLoading ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="space-y-2 rounded-lg border p-4">
                                <Skeleton className="h-48 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                                <Skeleton className="h-3 w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {relatedPosts.map((relatedPost) => (
                            <article
                                key={relatedPost.id}
                                className="group rounded-lg border p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    router.push(`/${locale}${blogPath}/${relatedPost.slug}`);
                                }}
                            >
                                {/* Featured Image */}
                                {relatedPost._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
                                    <div className="relative mb-3 aspect-video overflow-hidden rounded-md">
                                        <img
                                            src={relatedPost._embedded['wp:featuredmedia'][0].source_url}
                                            alt={relatedPost._embedded['wp:featuredmedia'][0].alt_text || ''}
                                            className="object-cover w-full h-full transition-transform group-hover:scale-105"
                                        />
                                    </div>
                                )}

                                {/* Title */}
                                <h3
                                    className="font-medium mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400"
                                    dangerouslySetInnerHTML={{ __html: relatedPost.title.rendered }}
                                />
                                {/* Date */}
                                <time className="text-sm text-gray-500 dark:text-gray-400">
                                    {new Date(relatedPost.date).toLocaleDateString(locale, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </time>

                                {/* Meta Description */}
                                {relatedPost.yoast_head_json?.og_description && (
                                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-3">
                                        {relatedPost.yoast_head_json?.og_description}
                                    </p>
                                )}
                            </article>
                        ))}

                        {relatedPosts.length === 0 && (
                            <div className="col-span-full text-center py-8">
                                <p className="text-gray-500 dark:text-gray-400">
                                    {t('noRelatedPosts')}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </FullscreenModal>
        </>
    );
}
